import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
} from "firebase/firestore";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { trackEvent } from "../../analytics/store/analyticsSlice";
import { db } from "../../../config/firebase";
import { RootState } from "../../../store/rootReducer";
import { AgentContext, GenericAgentConversation } from "../types/agentConfig";
import { GenericAgentConversationService } from "../services/GenericAgentConversationService";
import {
  setConversationsLoading,
  setConversationsSuccess,
  setConversationsError,
} from "../store/genericAgentConversationsSlice";
import {
  selectConversationsForContext,
  selectConversationsLoadingStatus,
  selectConversationsError,
} from "../store/genericAgentConversationsSelectors";

/**
 * Generic hook for managing agent conversations
 */
export function useGenericAgentConversations(context: AgentContext) {
  const dispatch = useDispatch();
  const unsubscribeRef = useRef<Unsubscribe | undefined>(undefined);

  const conversations = useSelector((state: RootState) =>
    selectConversationsForContext(
      state, 
      context.agentType, 
      context.contextId, 
      context.userId
    )
  );

  const loadingStatus = useSelector((state: RootState) =>
    selectConversationsLoadingStatus(
      state, 
      context.agentType, 
      context.contextId, 
      context.userId
    )
  );

  const error = useSelector((state: RootState) =>
    selectConversationsError(
      state, 
      context.agentType, 
      context.contextId, 
      context.userId
    )
  );

  useEffect(() => {
    // Track page view for agent conversations
    dispatch(
      trackEvent({
        name: `${context.agentType}_agent_conversations_viewed`,
        params: { agentType: context.agentType },
      })
    );

    // Set loading state
    dispatch(setConversationsLoading({
      agentType: context.agentType,
      contextId: context.contextId,
      userId: context.userId,
    }));

    try {
      // Create service instance
      const service = new GenericAgentConversationService(context.agentType);
      const conversationsPath = service.getConversationsPath(context);

      // Set up real-time listener
      const conversationsQuery = query(
        collection(db, conversationsPath),
        orderBy("createdAt", "desc")
      );

      unsubscribeRef.current = onSnapshot(
        conversationsQuery,
        (snapshot) => {
          const conversationsData: GenericAgentConversation[] = [];
          
          snapshot.forEach((doc) => {
            conversationsData.push({
              id: doc.id,
              contextId: context.contextId,
              agentType: context.agentType,
              metadata: doc.data() as GenericAgentConversation['metadata'],
            });
          });

          dispatch(setConversationsSuccess({
            agentType: context.agentType,
            contextId: context.contextId,
            userId: context.userId,
            conversations: conversationsData,
          }));
        },
        (error) => {
          console.error("Error fetching conversations:", error);
          dispatch(setConversationsError({
            agentType: context.agentType,
            contextId: context.contextId,
            userId: context.userId,
            error: error.message,
          }));
        }
      );
    } catch (error) {
      console.error("Error setting up conversations listener:", error);
      dispatch(setConversationsError({
        agentType: context.agentType,
        contextId: context.contextId,
        userId: context.userId,
        error: error instanceof Error ? error.message : "Unknown error",
      }));
    }

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [
    context.agentType, 
    context.contextId, 
    context.userId, 
    dispatch
  ]);

  return {
    conversations,
    loadingStatus,
    error,
  };
}